// 后端 eligibility Vo → 前端展示契约的适配层（纯函数，可单测）。
// 后端 status 是逗号分隔多状态串（首 token 为 primary）；金额美元 → 展示层美分。
import dayjs from 'utils/dayjs';
import {
    EBenefitPeriod,
    ECoverageLevel,
    ECoverageStatus,
    EEligibilityOutcome,
    ELIGIBILITY_REJECTION_STATUSES,
    ENetworkStatus,
    TAccumulatorTile,
    TBackendBenefitPeriod,
    TBackendDiscoveryCandidate,
    TBackendEligibilityCheckVo,
    TBackendEligibilityDetail,
    TBackendEligibilityHistoryItem,
    TBackendNetworkTier,
    TBenefitScope,
    TEligibilityCheckerRole,
    TEligibilityCheckHistoryRow,
    TEligibilityCheckVo,
    TEligibilityResult,
    TPayerCandidate,
    TVisitCostTile,
} from 'types/insuranceEligibility';

export const withZone = (t?: string): string | undefined => {
    if (!t || !/\d:\d/.test(t)) return t;
    return /(Z|[+-]\d{2}:?\d{2})$/.test(t) ? t : dayjs.utc(t).toISOString();
};

export const splitStatusTokens = (status?: string | null): string[] =>
    (status || '')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

// UNMAPPED：请求被挡在发出之前，不构成结果卡——走 payerUnmatched / advanced search 流
export const isUnmappedStatus = (vo?: TBackendEligibilityCheckVo | null): boolean =>
    splitStatusTokens(vo?.status)[0] === 'UNMAPPED';

const dollarsToCents = (v?: number | null): number | undefined => (v == null ? undefined : Math.round(v * 100));

// 后端所有 coinsurance 百分比（单值 coinsurancePercent 与冲突行 benefitPercent）都是 0-1 小数
// （0.2 = 20%，2026-09-16 与后端确认）→ 前端整数百分比
const percentToInt = (v?: number | null): number | undefined => (v == null ? undefined : Math.round(v * 100));

// 后端 per-tile scope token → 前端枚举（同拼写直转，未知值保持沉默——scope 行宁缺勿错）
const PERIOD_BY_TOKEN: Record<string, EBenefitPeriod> = {
    VISIT: EBenefitPeriod.VISIT,
    CALENDAR_YEAR: EBenefitPeriod.CALENDAR_YEAR,
    SERVICE_YEAR: EBenefitPeriod.SERVICE_YEAR,
    // 仅 visitLimitTimeQualifier 会给（34→MONTH）
    MONTH: EBenefitPeriod.MONTH,
};

const tileScope = (
    period?: TBackendBenefitPeriod,
    networkTier?: TBackendNetworkTier,
    coverageLevel?: ECoverageLevel,
): TBenefitScope | undefined => {
    const scope: TBenefitScope = { coverageLevel };
    if (period && PERIOD_BY_TOKEN[period]) scope.period = PERIOD_BY_TOKEN[period];
    if (networkTier === 'IN_NETWORK') scope.networkTier = ENetworkStatus.IN_NETWORK;
    else if (networkTier === 'OUT_OF_NETWORK') scope.networkTier = ENetworkStatus.OUT_OF_NETWORK;
    if (!scope.period && !scope.networkTier && !scope.coverageLevel) return undefined;
    return scope;
};

const amountTile = (dollars?: number | null, scope?: TBenefitScope, note?: string | null): TVisitCostTile =>
    dollars == null
        ? { reported: false }
        : { reported: true, kind: 'AMOUNT', values: [Math.round(dollars * 100)], scope, note: note || undefined };

const percentTile = (percent?: number | null, scope?: TBenefitScope, note?: string | null): TVisitCostTile =>
    percent == null
        ? { reported: false }
        : {
              reported: true,
              kind: 'PERCENT',
              values: [percentToInt(percent) as number],
              scope,
              note: note || undefined,
          };

// individual 级累计额度：met/remaining 优先用 payer 真值；缺的那条腿在算术无歧义时由另两条补算
// 并标 derived（前端 "(calculated)"）。总额缺失时整卡 Not reported（原型 present=total，
// 只有 met/remaining 的行不硬凑主数字）
const accumulatorTile = (
    totalDollars?: number | null,
    metDollars?: number | null,
    remainingDollars?: number | null,
    period?: TBackendBenefitPeriod,
    networkTier?: TBackendNetworkTier,
): TAccumulatorTile => {
    if (totalDollars == null && metDollars == null && remainingDollars == null) return { reported: false };
    const totalCents = dollarsToCents(totalDollars);
    let remainingCents = dollarsToCents(remainingDollars);
    let metCents = dollarsToCents(metDollars);
    let derived: 'MET' | 'REMAINING' | undefined;
    if (metCents == null && totalCents != null && remainingCents != null) {
        metCents = Math.max(0, totalCents - remainingCents);
        derived = 'MET';
    } else if (remainingCents == null && totalCents != null && metCents != null) {
        remainingCents = Math.max(0, totalCents - metCents);
        derived = 'REMAINING';
    }
    return {
        reported: totalCents != null,
        totalCents,
        remainingCents,
        metCents,
        derived,
        scope: tileScope(period, networkTier, ECoverageLevel.INDIVIDUAL),
    };
};

// CONFLICTING_AMOUNTS：冲突原始行 → tile 多值（COPAY 美元→美分；COINSURANCE 0-1 小数→整数
// 百分比），每值带 payer 原文标签（additionalInformationDescription；该行没附则 null）
const conflictValues = (
    detail: TBackendEligibilityDetail | undefined,
    field: 'COPAY' | 'COINSURANCE',
): { values: number[]; labels: Array<string | null> } | undefined => {
    const group = detail?.conflictBenefits?.find((c) => c.field === field);
    if (!group?.values?.length) return undefined;
    const rows = group.values
        .map((v) => ({
            value: field === 'COPAY' ? dollarsToCents(v.benefitAmount) : percentToInt(v.benefitPercent),
            label: v.additionalInformationDescription || null,
        }))
        .filter((r): r is { value: number; label: string | null } => r.value != null);
    if (rows.length < 2) return undefined;
    return { values: rows.map((r) => r.value), labels: rows.map((r) => r.label) };
};

const adaptResult = (
    tokens: string[],
    detail?: TBackendEligibilityDetail | null,
    // PROVIDER_NO_MATCHING_PLAN 时的 plan type 展示名（其余原因为 undefined）
    planTypeNotOnTemplate?: string,
): TEligibilityResult => {
    const d = detail || {};
    const primary = tokens[0];
    let coverageStatus = ECoverageStatus.UNKNOWN;
    if (primary === 'INACTIVE') coverageStatus = ECoverageStatus.INACTIVE;
    else if (tokens.length) coverageStatus = ECoverageStatus.ACTIVE;

    const result: TEligibilityResult = { coverageStatus };

    // 271 的 payer 名：BH 块 / SNC 行点名（展示层过 payerProse 处理全大写与档案拼写）
    if (d.payerName) result.payer = { name: d.payerName };

    if (d.planName || d.groupNumber || d.planEffectiveStart || d.planEffectiveEnd) {
        result.plan = {
            name: d.planName,
            group: d.groupNumber,
            startDate: d.planEffectiveStart,
            endDate: d.planEffectiveEnd,
        };
    }

    // INACTIVE 不渲染 tiles（老契约同规则）
    if (coverageStatus !== ECoverageStatus.INACTIVE) {
        const copay = amountTile(d.copayAmount, tileScope(d.copayPeriod, d.copayNetworkTier), d.copayNote);
        const coinsurance = percentTile(
            d.coinsurancePercent,
            tileScope(d.coinsurancePeriod, d.coinsuranceNetworkTier),
            d.coinsuranceNote,
        );
        // 一般医疗门诊那一行（STC=98）。后端 conflictBenefits 只分 COPAY / COINSURANCE 两组，
        // 没有 medical 的冲突分组，所以这一格不参与下面的冲突覆盖——它不可能是多值
        const medicalCopay = amountTile(
            d.medicalCopayAmount,
            tileScope(d.medicalCopayPeriod, d.medicalCopayNetworkTier),
            d.medicalCopayNote,
        );
        // CONFLICTING_AMOUNTS：冲突原始行覆盖单值，tile 渲染 "$30 or $60 · payer sent both"；
        // 每值的 payer 原文标签随 valueLabels 下发（冲突面板逐行引用）
        const copayConflict = conflictValues(d, 'COPAY');
        if (copayConflict) {
            copay.reported = true;
            copay.kind = 'AMOUNT';
            copay.values = copayConflict.values;
            copay.valueLabels = copayConflict.labels;
        }
        const coinsuranceConflict = conflictValues(d, 'COINSURANCE');
        if (coinsuranceConflict) {
            coinsurance.reported = true;
            coinsurance.kind = 'PERCENT';
            coinsurance.values = coinsuranceConflict.values;
            coinsurance.valueLabels = coinsuranceConflict.labels;
        }
        result.costSharing = {
            copay,
            medicalCopay,
            coinsurance,
            // GENERAL/MIXED → tiles 下方琥珀警示（BEHAVIORAL/null 沉默）
            basis: d.costSharingBasis || undefined,
            deductible: accumulatorTile(
                d.deductibleIndividualTotal,
                d.deductibleIndividualMet,
                d.deductibleIndividualRemaining,
                d.deductibleIndividualPeriod,
                d.deductibleIndividualNetworkTier,
            ),
            oopMax: accumulatorTile(
                d.oopIndividualTotal,
                d.oopIndividualMet,
                d.oopIndividualRemaining,
                d.oopIndividualPeriod,
                d.oopIndividualNetworkTier,
            ),
        };
    }

    const carveOut =
        d.carveOutMarker || tokens.includes('CARVE_OUT') ? { administratorName: d.carveOutMarker } : undefined;
    if (d.bhCovered != null || carveOut || d.visitLimitTotal != null) {
        result.behavioralHealth = {
            covered: !!d.bhCovered,
            serviceType: d.bhServiceType || undefined,
            // period：VISIT 只表达单位（展示同缺省，BhBlock 判定），其余 token 转枚举
            visitLimit:
                d.visitLimitTotal != null
                    ? {
                          quantity: d.visitLimitTotal,
                          period: d.visitLimitTimeQualifier ? PERIOD_BY_TOKEN[d.visitLimitTimeQualifier] : undefined,
                      }
                    : undefined,
            carveOut,
        };
    }

    if (tokens.includes('OUT_OF_NETWORK') || d.networkStatus) {
        let networkStatus = ENetworkStatus.UNKNOWN;
        if (tokens.includes('OUT_OF_NETWORK') || d.networkStatus === 'OUT_OF_NETWORK') {
            networkStatus = ENetworkStatus.OUT_OF_NETWORK;
        } else if (d.networkStatus === 'IN_NETWORK') networkStatus = ENetworkStatus.IN_NETWORK;
        // payer 原文（raw_response 派生）：OON 信息行渲染为 "The payer states ...: \"...\""
        result.network = { status: networkStatus, payerMessage: d.networkMessage || undefined };
        // 平台按 service template 判的 OON（payer 判 active）：带上 plan type，文案点名原因而不是怪 payer
        if (networkStatus === ENetworkStatus.OUT_OF_NETWORK && planTypeNotOnTemplate) {
            result.network.planTypeNotOnTemplate = planTypeNotOnTemplate;
        }
    }

    // null = payer 未答 → 整个字段缺省（absence ≠ not required，FR11）
    if (d.priorAuthRequired != null) result.priorAuth = { required: d.priorAuthRequired };

    if (tokens.includes('SERVICE_NOT_COVERED')) result.serviceNotCovered = { note: d.payerNote };

    return result;
};

// 后端 check Vo → 展示模型。UNMAPPED / 无 status 返回 undefined（无结果卡），由调用方分流
export const adaptCheckVo = (vo?: TBackendEligibilityCheckVo | null): TEligibilityCheckVo | undefined => {
    const tokens = splitStatusTokens(vo?.status);
    if (!tokens.length || tokens[0] === 'UNMAPPED') return undefined;

    const checkedAt = withZone(vo?.latestCheckedAt) || new Date().toISOString();
    // "Last checked by" 用实际操作人（owner 代查显示 owner）；仅 GP 路径下发，solo 恒空。
    // SYSTEM_AUTO_* 触发时 operator 为 null → 不带 by，回退纯时间标注
    const checkedBy =
        `${vo?.latestCheckedOperatorFirstName || ''} ${vo?.latestCheckedOperatorLastName || ''}`.trim() || undefined;
    const rejectionTokens = tokens.filter((t) => ELIGIBILITY_REJECTION_STATUSES.has(t));

    // 被查保险的快照随每一条结果走（拒识行也有：那次请求同样是对着某一份保险发的）
    const carrier = vo?.carrier || undefined;
    const plan = vo?.plan || undefined;
    const insuranceId = vo?.insuranceId ?? undefined;

    if (ELIGIBILITY_REJECTION_STATUSES.has(tokens[0])) {
        return {
            checkedAt,
            checkedBy,
            carrier,
            plan,
            insuranceId,
            statusTokens: tokens,
            outcome: EEligibilityOutcome.REJECTED,
            rejection: { codes: rejectionTokens.length ? rejectionTokens : [tokens[0]] },
        };
    }

    return {
        checkedAt,
        checkedBy,
        carrier,
        plan,
        insuranceId,
        statusTokens: tokens,
        outcome: EEligibilityOutcome.OK,
        result: adaptResult(
            tokens,
            vo?.detail,
            vo?.networkStatusReason === 'PROVIDER_NO_MATCHING_PLAN'
                ? vo.networkStatusReasonPlanType || undefined
                : undefined,
        ),
    };
};

// check history 行 → 展示模型。角色未知值一律不渲染徽章（后端新增角色时不会印出生字符串）
const HISTORY_ROLES = new Set([
    'GROUP_OWNER',
    'GROUP_PROVIDER',
    'GROUP_PRACTICE_MANAGER',
    'GROUP_ADMINISTRATIVE_ASSISTANT',
    'GROUP_CLINICAL_ASSISTANT',
    'SYSTEM',
]);

const joinName = (first?: string | null, last?: string | null): string | undefined =>
    `${first || ''} ${last || ''}`.trim() || undefined;

export const adaptHistoryRows = (list?: TBackendEligibilityHistoryItem[] | null): TEligibilityCheckHistoryRow[] =>
    (list || []).map((v) => {
        const role = (v.checkerRole || '').toUpperCase();
        return {
            id: v.id,
            checkedAt: withZone(v.checkedAt) || '',
            status: v.resultStatus || undefined,
            carrier: v.carrier || undefined,
            plan: v.plan || undefined,
            insuranceCardType: v.insuranceCardType || undefined,
            checkerName: joinName(v.checkerFirstName, v.checkerLastName),
            checkerRole: HISTORY_ROLES.has(role) ? (role as TEligibilityCheckerRole) : undefined,
            targetProviderName: joinName(v.targetProviderFirstName, v.targetProviderLastName),
        };
    });

// Discovery 候选 → 前端候选卡（planType 作说明行）
export const adaptDiscoveryCandidates = (list?: TBackendDiscoveryCandidate[] | null): TPayerCandidate[] =>
    (list || []).map((c) => ({ name: c.payerName, payerId: c.payerId, note: c.planType || undefined }));
